/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

/** This is about trying to automatically apply copyedit rules to the document, from:
 * https://healthendeavors.sharepoint.com/:w:/r/sites/MedPearl/_layouts/15/doc2.aspx?action=edit&sourcedoc=%7B448fd2a4-4c2e-46af-92ed-0b636ca5cbe9%7D&wdExp=TEAMS-TREATMENT&web=1
 */
type HeadingLevel = 1 | 2;

const COPY_EDIT_HEADINGS: Record<string, HeadingLevel> = {
  Evaluation: 1,
  'First-line considerations': 1,
  Pointers: 1,
  'Urgent (1-2 wk)': 1,
  'Routine (1-2 mo)': 1,
  'Patient resources': 1,
  'Provider resources': 1,
  'Further reading': 1,
  History: 2,
  Exam: 2,
  Medications: 2,
  Treatment: 2,
  Prevention: 2,
  Labs: 2,
  Imaging: 2,
};

const NORMALIZED_COPY_EDIT_HEADINGS = Object.fromEntries(
  Object.entries(COPY_EDIT_HEADINGS).map(([text, level]) => [text.toLowerCase(), { text, level }])
) as Record<string, { text: string; level: HeadingLevel }>;

function replaceHeadingLevel(element: Element, tagName: 'h1' | 'h2'): void {
  const heading = document.createElement(tagName);
  Array.from(element.attributes).forEach((attribute) => heading.setAttribute(attribute.name, attribute.value));
  while (element.firstChild) {
    heading.appendChild(element.firstChild);
  }
  element.replaceWith(heading);
}

/** Apply automated copyedit rules to document content. */
export function updateCopyEdit(node: HTMLElement): void {
  node.querySelectorAll('h3').forEach((element) => replaceHeadingLevel(element, 'h1'));
  node.querySelectorAll('h4').forEach((element) => replaceHeadingLevel(element, 'h2'));

  node.querySelectorAll('h1 strong, h2 strong, h3 strong, h4 strong, h5 strong, h6 strong').forEach((element) => {
    const heading = element.closest('h1, h2, h3, h4, h5, h6');
    const action = element.closest('action');
    if (!action || !heading?.contains(action)) element.replaceWith(...Array.from(element.childNodes));
  });

  node.querySelectorAll('h2 > span:only-child').forEach((element) => {
    if (element.parentElement?.childNodes.length === 1) element.replaceWith(...Array.from(element.childNodes));
  });

  node.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((element) => {
    if (element.firstChild?.nodeType === Node.TEXT_NODE) {
      element.firstChild.textContent = element.firstChild.textContent?.replace(/^[\s\u00a0]+/, '') || '';
    }
    if (element.lastChild?.nodeType === Node.TEXT_NODE) {
      element.lastChild.textContent = element.lastChild.textContent?.replace(/[\s\u00a0]+$/, '') || '';
    }
  });

  node.querySelectorAll('h1, h2, div').forEach((element) => {
    const normalizedText = element.textContent
      ?.replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/:$/, '')
      .toLowerCase();
    const copyEditHeading = normalizedText ? NORMALIZED_COPY_EDIT_HEADINGS[normalizedText] : undefined;
    const isBoldDiv =
      element.tagName === 'DIV' && element.children.length === 1 && element.firstElementChild?.tagName === 'STRONG';
    if (!copyEditHeading || (element.tagName === 'DIV' && !isBoldDiv) || element.querySelector('a, action')) {
      return;
    }

    const heading = document.createElement(`h${copyEditHeading.level}`);
    heading.textContent = copyEditHeading.text;
    element.replaceWith(heading);
  });
}
